import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, Percent, Users, MessageCircle,
  FileSpreadsheet, Bell, Search, Eye, AlertTriangle,
} from "lucide-react";
import ReportDrawer from "@/components/ReportDrawer";
import WhatsAppReminderDialog from "@/components/WhatsAppReminderDialog";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";

const KPI = ({ label, value, icon: Icon, tone, testId }) => (
  <Card data-testid={testId} className="p-5 bg-[#121215] border-[#27272a] card-hover">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="font-display text-3xl font-bold mt-2">{value}</div>
      </div>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
  </Card>
);

function StatusPill({ status }) {
  const map = {
    submitted: { c: "bg-emerald-500/12 text-emerald-300 border-emerald-500/30", d: "bg-emerald-400", l: "Submitted" },
    missed: { c: "bg-rose-500/12 text-rose-300 border-rose-500/30", d: "bg-rose-400", l: "Missed" },
    holiday: { c: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30", d: "bg-zinc-400", l: "Holiday" },
  };
  const m = map[status] || map.holiday;
  return (
    <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-xs ${m.c}`}>
      <span className={`status-dot ${m.d}`} /> {m.l}
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [day, setDay] = useState(format(new Date(), "yyyy-MM-dd")); // local date, not UTC
  const [remindOpen, setRemindOpen] = useState(false);
  const [delivery, setDelivery] = useState("click");
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawerReport, setDrawerReport] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/dashboard/daily", { params: { day } });
    setData(data);
  };

  useEffect(() => { load(); }, [day]);
  useEffect(() => {
    api.get("/settings").then((r) => setDelivery(r.data.whatsapp_delivery || "click")).catch(() => {});
  }, []);
  // Opened from the daily reminder nudge (/?remind=1)
  useEffect(() => {
    if (searchParams.get("remind") === "1") {
      setRemindOpen(true);
      searchParams.delete("remind"); setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      if (filter !== "all" && r.status !== filter) return false;
      if (q && !r.manager.name.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [data, filter, q]);

  const remind = async (mid) => {
    if (delivery === "click") { setRemindOpen(true); return; }
    try {
      const { data } = await api.post(`/alerts/whatsapp-reminder/${mid}`);
      toast.success(`Reminder queued → ${data.target}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendDigest = async () => {
    try {
      const { data } = await api.post("/alerts/daily-digest");
      const label = data.status === "sent" ? "Digest sent via WhatsApp" : data.status === "mocked" ? "Digest recorded (Twilio not configured)" : "Digest attempted";
      toast.success(label, { description: data.message });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runReminders = async () => {
    if (delivery === "click") { setRemindOpen(true); return; }
    try {
      const { data } = await api.post("/alerts/run-reminders");
      const desc = `sent: ${data.sent} · mocked: ${data.mocked} · failed: ${data.failed}`;
      if (data.skipped) {
        toast.message("No reminders fired", { description: data.skipped });
        return;
      }
      const label = data.twilio_enabled
        ? `WhatsApp reminders fired for ${data.total_missing} manager(s)`
        : `Twilio not configured — reminders logged for ${data.total_missing} manager(s)`;
      toast.success(label, { description: desc });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runEscalations = async () => {
    try {
      const { data } = await api.post("/alerts/run-escalations");
      setEscalations(data.detail || []);
      const label = data.escalations_fired
        ? `${data.escalations_fired} escalation(s) fired (threshold: ${data.threshold})`
        : `No escalations — no manager has missed ≥${data.threshold} working days`;
      toast.success(label);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openReport = async (rid) => {
    const { data } = await api.get(`/reports/${rid}`);
    setDrawerReport(data);
  };

  const exportExcel = () => {
    const d = new Date(day);
    const token = localStorage.getItem("eod_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/monthly.xlsx?year=${d.getFullYear()}&month=${d.getMonth() + 1}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("export failed"); return r.blob(); })
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `eod-compliance-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      })
      .catch(() => toast.error("Export failed"));
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Daily Dashboard</div>
          <h1 className="font-display text-3xl font-bold">{format(new Date(day), "EEEE, MMM d, yyyy")}</h1>
          <div className="text-sm text-zinc-400 mt-1">
            {data?.is_working_day ? "Working day" : "Non-working day"} · {data?.kpis?.total ?? "…"} managers tracked
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input type="date" value={day} onChange={(e) => setDay(e.target.value)}
            data-testid="dashboard-date-picker"
            className="w-44 bg-[#121215] border-[#27272a]" />
          {isAdmin && (
            <>
              <Button onClick={sendDigest} data-testid="send-digest-button" variant="outline"
                className="bg-[#121215] border-[#27272a] hover:bg-[#18181b]">
                <Bell className="w-4 h-4 mr-2 text-indigo-400" /> Send Digest
              </Button>
              <Button onClick={runReminders} data-testid="run-reminders-button" variant="outline"
                className="bg-[#121215] border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Missing
              </Button>
              <Button onClick={runEscalations} data-testid="run-escalations-button" variant="outline"
                className="bg-[#121215] border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
                <AlertTriangle className="w-4 h-4 mr-2" /> Escalations
              </Button>
              <Button onClick={exportExcel} data-testid="export-excel-button"
                className="bg-emerald-500/90 hover:bg-emerald-500 text-white">
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Export Excel
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Submitted today" value={data?.kpis?.submitted ?? "—"} icon={CheckCircle2}
          tone="bg-emerald-500/15 text-emerald-400" testId="kpi-submitted-card" />
        <KPI label="Missed today" value={data?.kpis?.missed ?? "—"} icon={XCircle}
          tone="bg-rose-500/15 text-rose-400" testId="kpi-missed-card" />
        <KPI label="Compliance today" value={data ? `${data.kpis.compliance_pct}%` : "—"} icon={Percent}
          tone="bg-indigo-500/15 text-indigo-400" testId="kpi-compliance-card" />
        <KPI label="Managers tracked" value={data?.kpis?.total ?? "—"} icon={Users}
          tone="bg-amber-500/15 text-amber-400" testId="kpi-managers-card" />
      </div>

      <Card className="bg-[#121215] border-[#27272a]">
        <div className="p-5 border-b border-[#27272a] flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold">Manager submissions</h2>
            <div className="text-xs text-zinc-500">Click any row to preview the report link and AI summary.</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <Input placeholder="Search manager…" value={q} onChange={(e) => setQ(e.target.value)}
                data-testid="dashboard-search-input"
                className="pl-9 h-9 w-56 bg-[#0f0f12] border-[#27272a]" />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="dashboard-status-filter" className="w-40 h-9 bg-[#0f0f12] border-[#27272a]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#121215] border-[#27272a]">
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="missed">Missed</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="divide-y divide-[#1f1f23]">
          {rows.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-500">No managers match your filters.</div>
          )}
          {rows.map((r) => (
            <div key={r.manager.id} data-testid={`manager-row-${r.manager.id}`}
              className="p-4 flex items-center gap-4 hover:bg-[#18181b] transition-colors">
              <div className="w-9 h-9 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 flex items-center justify-center text-xs font-medium">
                {r.manager.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium truncate">{r.manager.name}</div>
                  <Badge variant="outline" className="border-[#3f3f46] text-zinc-400 text-[10px]">{r.manager.role_label}</Badge>
                </div>
                <div className="text-xs text-zinc-500 truncate">{r.manager.email}</div>
              </div>
              <div className="hidden sm:block">
                {r.latest_report && (
                  <span className="text-xs text-zinc-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {format(new Date(r.latest_report.received_at), "HH:mm")}
                  </span>
                )}
              </div>
              <StatusPill status={r.status} />
              <div className="flex items-center gap-1">
                {r.latest_report && (
                  <Button size="sm" variant="ghost" onClick={() => openReport(r.latest_report.id)}
                    data-testid={`report-preview-button-${r.manager.id}`}
                    className="text-zinc-300 hover:text-white">
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Button>
                )}
                {r.status === "missed" && isAdmin && (
                  <Button size="sm" variant="outline" onClick={() => remind(r.manager.id)}
                    data-testid={`whatsapp-alert-button-${r.manager.id}`}
                    className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10">
                    <MessageCircle className="w-4 h-4 mr-1" /> Remind
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <WhatsAppReminderDialog open={remindOpen} onOpenChange={setRemindOpen} day={day} />
      <ReportDrawer open={!!drawerReport} onOpenChange={(o) => !o && setDrawerReport(null)} report={drawerReport} />
    </div>
  );
}
